import { API_CONFIG } from "@/types/models/apiConfig";
import { performEmergencySessionCleanup } from "@/lib/auth/sessionRecovery";
import type { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from "axios";
import axios, { AxiosError } from "axios";
import { extractErrorDetails } from "./clientUtils";
import { toast } from "sonner";
import https from "https";

// Extend AxiosRequestConfig to include _retry property
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
    _retry?: boolean;
    _retryCount?: number;
    _skipAuth?: boolean; // Auth token eklemeyi skip et (login, register vb. için)
}

/**
 * Axios instance oluştur ve yapılandır
 * Hem browser'da hem SSR'da API_CONFIG.BASE_URL kullan
 */
export const axiosInstance = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    withCredentials: true,
    // Proxy'yi devre dışı bırak (url.parse deprecation warning'ini önler)
    proxy: false,
    // Development ortamında self-signed sertifikaları kabul et
    // SADECE localhost backend için güvenli
    ...(process.env.NODE_ENV === 'development' && typeof window === 'undefined' && {
        httpsAgent: new https.Agent({
            rejectUnauthorized: false
        })
    })
});

// Refresh token durumu takibi - Race condition önleme
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error?: unknown) => void;
}> = [];

type BrowserSession = { user?: { email?: string | null; id?: string } } | null;

const getAuthSession = async (): Promise<BrowserSession> => {
    if (typeof window === 'undefined') {
        return null;
    }

    const response = await fetch('/api/platform-session/current', {
        credentials: 'same-origin',
        cache: 'no-store',
    });

    if (!response.ok) {
        return null;
    }

    const session = await response.json();
    return session?.user ? session : null;
};

/**
 * Kuyruktaki bekleyen istekleri işle
 */
const processQueue = (error: unknown, token?: string) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * İsteğin retry edilebilir olup olmadığını kontrol et
 */
const isRetryableRequest = (config: ExtendedAxiosRequestConfig): boolean => {
    const method = config.method?.toUpperCase();
    const retryMethods = ['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE'];
    return method ? retryMethods.includes(method) : false;
};

/**
 * Browser requests authenticate through the same-origin BFF. Platform access
 * and refresh tokens are never copied into the JavaScript-visible session.
 */
const addAuthToken = async (config: ExtendedAxiosRequestConfig): Promise<void> => {
    if (typeof window !== 'undefined' && config.method?.toUpperCase() !== 'GET') {
        if (!config.headers) config.headers = {} as AxiosRequestHeaders;
        (config.headers as AxiosRequestHeaders)['X-Turkwise-BFF'] = '1';
    }

    // Auth endpoint'leri için token ekleme (login, register, refresh)
    const isAuthEndpoint =
        config.url?.includes('/auth/login') ||
        config.url?.includes('/auth/register') ||
        config.url?.includes('/auth/refresh') ||
        config._skipAuth;

    if (isAuthEndpoint) {
        if (API_CONFIG.DEBUG) {
            console.log('[Auth] Skipping token for auth endpoint:', config.url);
        }
        return; // Auth endpoint'leri için token ekleme
    }

};

/**
 * İsteğin retry edilebilir hata olup olmadığını kontrol et
 */
const isRetryableError = (error: AxiosError): boolean => {
    if (!error.response) {
        // Network errors, timeout vb.
        return error.code !== 'ECONNABORTED' && error.code !== 'ERR_CANCELED';
    }

    const status = error.response.status;
    // 5xx hatalar ve 429 (rate limit) için retry yap
    return status >= 500 || status === 429;
};

/**
 * Retry delay hesapla (exponential backoff)
 */
const calculateRetryDelay = (retryCount: number): number => {
    const baseDelay = API_CONFIG.RETRY_DELAY;
    const maxDelay = API_CONFIG.MAX_RETRY_DELAY;
    const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);
    // Jitter ekle (0-100ms arası rastgele gecikme)
    return delay + Math.random() * 100;
};

// Request interceptor
axiosInstance.interceptors.request.use(
    async (config) => {
        // Auth state is resolved by the same-origin Platform session endpoint.
        await addAuthToken(config as ExtendedAxiosRequestConfig);

        const method = config.method?.toUpperCase();

        // GET olmayan isteklerde Content-Type kontrolü
        if (method && method !== "GET" && method !== "HEAD") {
            if (config.headers) {
                const hasContentType = Object.keys(config.headers).some(
                    (k) => k.toLowerCase() === "content-type"
                );
                if (!hasContentType) {
                    (config.headers as AxiosRequestHeaders)["Content-Type"] = "application/json";
                }
            }

            // Body yoksa boş bir body ekle (POST/PUT için)
            if (typeof config.data === "undefined" && (method === "POST" || method === "PUT")) {
                config.data = {};
            }
        }

        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

/**
 * Refresh başarısız olduğunda kullanıcıyı login'e yönlendir
 */
const redirectToLogin = (error: unknown) => {
    processQueue(error);

    if (typeof window !== 'undefined') {
        // Emergency session cleanup yap
        performEmergencySessionCleanup();

        // Mevcut URL'i kaydet ki login sonrası geri dönebilsin
        const currentPath = window.location.pathname + window.location.search;
        const returnUrl = encodeURIComponent(currentPath);
        window.location.href = `/login?returnUrl=${returnUrl}`;
    }
};

/**
 * 401 hatası yönetimi - Basit: Session'ı force refresh et
 */
const handle401Error = async (originalRequest: ExtendedAxiosRequestConfig): Promise<AxiosResponse> => {
    console.log('[Auth] 401 error detected for:', originalRequest.url);

    // Eğer zaten bir refresh işlemi devam ediyorsa, kuyruğa ekle
    if (isRefreshing) {
        console.log('[Auth] Refresh in progress, adding request to queue:', originalRequest.url);
        return new Promise((resolve, reject) => {
            failedQueue.push({
                resolve: () => resolve(axiosInstance(originalRequest)),
                reject
            });
        });
    }

    // Retry flag'ini set et
    originalRequest._retry = true;
    isRefreshing = true;

    try {
        console.log('[Auth] Starting Platform session validity check...');

        // Re-check the server-side Platform session.
        // JWT callback token'ın expire olduğunu görecek ve refresh yapacak
        const session = await getAuthSession();

        console.log('[Auth] Session refresh completed:', {
            hasSession: !!session,
            userEmail: session?.user?.email,
            userId: session?.user?.id,
        });

        if (session?.user) {
            console.log('[Auth] Session is active, retrying through BFF:', originalRequest.url);
            processQueue(null);
            return axiosInstance(originalRequest);
        } else {
            // Session yoksa veya token yoksa login'e yönlendir
            console.error('[Auth] No valid session after refresh, redirecting to login');
            redirectToLogin(new Error('No valid session after refresh'));
            return Promise.reject(new Error('No valid session after refresh'));
        }
    } catch (error) {
        console.error('[Auth] Session refresh failed:', error);
        redirectToLogin(error);
        return Promise.reject(error);
    } finally {
        isRefreshing = false;
    }
};

/**
 * Retry mekanizması - network hataları ve 5xx için
 */
const handleRetryableError = async (
    error: AxiosError,
    originalRequest: ExtendedAxiosRequestConfig
): Promise<AxiosResponse> => {
    const retryCount = originalRequest._retryCount ?? 0;

    if (retryCount >= API_CONFIG.MAX_RETRIES) {
        return Promise.reject(error);
    }

    originalRequest._retryCount = retryCount + 1;

    const delay = calculateRetryDelay(retryCount);

    if (API_CONFIG.DEBUG) {
        console.log(`[Retry] Attempt ${retryCount + 1}/${API_CONFIG.MAX_RETRIES} after ${delay}ms for ${originalRequest.url}`);
    }

    await new Promise(resolve => setTimeout(resolve, delay));

    return axiosInstance(originalRequest);
};

// Response interceptor - 401 ve retry yönetimi
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        // Backend ApiResponse<T> wrapper'ını unwrap et
        // Eğer response.data { success: boolean, data: T } formatında ise, T'yi return et
        if (response.data && typeof response.data === 'object' && 'data' in response.data && 'success' in response.data) {
            const wrappedData = response.data as { success: boolean; data: unknown };
            if (API_CONFIG.DEBUG) {
                console.log('[Axios] Unwrapping ApiResponse wrapper for:', response.config.url);
            }
            return {
                ...response,
                data: wrappedData.data,
            };
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest: ExtendedAxiosRequestConfig = error.config as ExtendedAxiosRequestConfig;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        // Error detaylarını çıkar ve logla
        const errorDetails = extractErrorDetails(error);
        if (API_CONFIG.DEBUG) {
            console.error('[API Error]', {
                url: originalRequest?.url,
                method: originalRequest?.method?.toUpperCase(),
                status: errorDetails.status,
                message: errorDetails.message,
                errors: errorDetails.errors,
                timestamp: new Date().toISOString()
            });
        }

        const isRefreshEndpoint = originalRequest.url?.includes('/api/auth/refresh');
        const isSessionEndpoint = originalRequest.url?.includes('/api/platform-session/current');
        const isLoginEndpoint = originalRequest.url?.includes('/api/auth/login');

        // 401 dışındaki tüm hataları kullanıcıya toast ile göster
        // Register ve login endpoint'lerinde de validation hatalarını göster
        if (typeof window !== 'undefined' &&
            errorDetails.status !== 401 &&
            !isRefreshEndpoint &&
            !isSessionEndpoint) {

            // Hata tipine göre farklı süre ve stil
            const duration = errorDetails.status && errorDetails.status >= 500 ? 6000 : 5000;

            // Hata mesajını göster
            toast.error(errorDetails.message, {
                duration,
                //description: errorDetails.errors?.length ? errorDetails.errors.join(', ') : undefined,
                dismissible: true,
            });
        }

        // 403 Forbidden - Mesajı göster (opsiyonel daha özel handling)
        if (error.response?.status === 403) {
            if (API_CONFIG.DEBUG) {
                console.warn('[403 Forbidden]', {
                    url: originalRequest?.url,
                    message: errorDetails.message,
                });
            }
            // Toast zaten yukarıda gösterilmiş
            return Promise.reject(error);
        }

        // Browser'da login/register sayfası kontrolü
        const isOnAuthPage = typeof window !== 'undefined' &&
            (window.location.pathname.includes('/login') ||
                window.location.pathname.includes('/register') ||
                window.location.pathname.includes('/forgot-password'));

        // 401 Unauthorized - Token refresh
        if (error.response?.status === 401) {

            // Eğer refresh endpoint'inde 401 alıyorsak, direkt logout yap
            if (isRefreshEndpoint) {
                console.error('[Auth] Refresh endpoint returned 401, forcing logout');
                if (typeof window !== 'undefined') {
                    // Session'ı temizle ve login'e yönlendir
                    const currentPath = window.location.pathname + window.location.search;
                    const returnUrl = encodeURIComponent(currentPath);
                    window.location.href = `/login?returnUrl=${returnUrl}`;
                }
                return Promise.reject(error);
            }

            // Diğer koşullar aynı kalır
            if (!originalRequest._retry &&
                !isLoginEndpoint &&
                !isOnAuthPage) {

                console.log('[Auth] 401 error detected for:', originalRequest.url);
                console.log('[Auth] Will attempt token refresh...');

                // Session endpoint özel durumu
                if (isSessionEndpoint && typeof window !== 'undefined') {
                    const hasCookies = document.cookie.length > 0;
                    console.log('[Auth] Session endpoint 401, has cookies:', hasCookies);
                    if (!hasCookies) {
                        const currentPath = window.location.pathname + window.location.search;
                        const returnUrl = encodeURIComponent(currentPath);
                        window.location.href = `/login?returnUrl=${returnUrl}`;
                        return Promise.reject(error);
                    }
                }

                return handle401Error(originalRequest);
            }
        }

        // Retry mekanizması - 5xx hatalar ve network errors için
        if (!originalRequest._retry && // Refresh retry'sı değilse
            isRetryableRequest(originalRequest) &&
            isRetryableError(error)) {
            return handleRetryableError(error, originalRequest);
        }

        return Promise.reject(error);
    }
);
